import React, { useEffect, useState } from 'react';
import { useTraffic } from '../context/TrafficContext';
import { EMPTY_STATE_COPY } from '../utils/isolationCopy';

// Per-user traffic isolation notices (docs/sdlc/per-user-traffic-isolation F2/F4/F5):
//   - Android IP-takeover warning / confirmation (WS `ip_takeover`)
//   - "No traffic from your devices yet" empty state (AC 118)
//   - the effective isolation mode, when GET /proxy/info reports it
// There is deliberately NO "this device is unowned" hint: under isolation=on the proxy never
// sends a user an unowned row, and the wire does not verifiably carry `owner`/a per-device
// unowned flag to the dashboard. The unowned cases are covered by the static-token tap
// warning (DeviceCapturePanel) and the report's excluded-rows line instead.
// Both capture paths: the empty state and hint apply to MITM and USB rows alike. The
// CTA opens Connect Device, which is where both a Wi-Fi enrollment and a USB tap
// enrollment are created.

const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || '').replace(/\/$/, '');

export function openConnectDevice() {
  try { window.dispatchEvent(new Event('adlens:open-connect-device')); } catch { /* non-browser */ }
}

/** GET /proxy/info `isolation`, or null when absent/unreachable (older proxy). */
export function useIsolationInfo() {
  const [iso, setIso] = useState(null);
  useEffect(() => {
    let dead = false;
    const base = PROXY_API_BASE || `http://${window.location.hostname}:8001`;
    fetch(`${base}/proxy/info`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!dead) setIso(d && d.isolation && typeof d.isolation === 'object' ? d.isolation : null); })
      .catch(() => { if (!dead) setIso(null); });
    return () => { dead = true; };
  }, []);
  return iso;
}

const box = (tone) => ({
  margin: '0', padding: '8px 16px', fontSize: 12, lineHeight: 1.5,
  background: `var(--${tone}-bg)`, color: 'var(--ink)', borderBottom: '1px solid var(--line)',
  display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
});
const btn = {
  fontSize: 11, padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
  border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--ink)',
};

/** Pure view, so tests render it without a TrafficProvider. */
export function IsolationNoticesView({
  notices = [], onDismiss = () => {}, isolation = null,
  connected = false, historyLoaded = false, totalRows = 0,
  onConnect = openConnectDevice,
}) {
  const effective = isolation && isolation.effective;
  const showEmpty = connected && historyLoaded && totalRows === 0 && effective === 'on';
  return (
    <div data-testid="isolation-notices">
      {notices.map((n) => (
        <div key={n.id} role="alert" data-testid={`takeover-${n.role}`} style={box(n.role === 'previous' ? 'warn' : 'info')}>
          <span>{n.text}</span>
          {n.role === 'previous' && <button type="button" style={btn} onClick={onConnect}>Re-enroll in Connect Device</button>}
          <button type="button" style={{ ...btn, marginLeft: 'auto' }} onClick={() => onDismiss(n.id)}>Dismiss</button>
        </div>
      ))}
      {showEmpty && (
        <div data-testid="isolation-empty" style={box('info')}>
          <span>{EMPTY_STATE_COPY}</span>
          <button type="button" style={btn} onClick={onConnect}>Open Connect Device</button>
        </div>
      )}
      {isolation && (
        <div data-testid="isolation-mode" style={{ padding: '2px 16px', fontSize: 10, color: 'var(--muted)', borderBottom: '1px solid var(--line)' }}>
          Traffic isolation: {isolation.effective}
          {isolation.configured && isolation.configured !== isolation.effective
            ? ` (configured ${isolation.configured}${isolation.fallbackReason ? `, refused: ${String(isolation.fallbackReason).replace(/_/g, ' ')}` : ''})`
            : ''}
          {effective === 'off' ? ' (all captured traffic is visible to every signed-in user)' : ''}
          {effective === 'shadow' ? ' (not enforced yet: you can still see other users\' rows)' : ''}
        </div>
      )}
    </div>
  );
}

export default function IsolationNotices() {
  const t = useTraffic() || {};
  const isolation = useIsolationInfo();
  const groups = t.liveTrafficGroups || {};
  const totalRows = Object.values(groups).reduce((s, rows) => s + (Array.isArray(rows) ? rows.length : 0), 0);
  return (
    <IsolationNoticesView
      notices={t.takeoverNotices || []}
      onDismiss={t.dismissTakeoverNotice}
      isolation={isolation}
      connected={t.wsStatus === 'connected'}
      historyLoaded={!!t.historyLoaded}
      totalRows={totalRows}
    />
  );
}
